import React, { useEffect, useRef, useState, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import LoginModal from './LoginModal';

/* ========================== HELPERS ========================== */
const prefixIfRelative = (url) => {
  if (!url) return '';
  if (/^https?:\/\//i.test(url)) return url;
  return `https://app.yaper.co${url.startsWith('/') ? '' : '/'}${url}`;
};

const isValidString = (s) =>
  typeof s === 'string' && s.trim() !== '' && s.trim() !== '""';

const normalizeDate = (raw) => {
  if (!isValidString(raw)) return { display: '', iso: '' };
  const parsed = new Date(raw);
  if (!Number.isNaN(parsed.getTime())) {
    return {
      display: parsed.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      }),
      iso: parsed.toISOString(),
    };
  }
  return { display: String(raw), iso: '' };
};

const stripBase64Images = (html) => {
  if (!html) return '';
  let cleaned = html.replace(/<img[^>]+src=["']data:[^"']+["'][^>]*\/?>/gi, '');
  cleaned = cleaned.replace(/<img[^>]+src=["']\s*["'][^>]*\/?>/gi, '');
  cleaned = cleaned.replace(/<h[2-4][^>]*>\s*(<br\s*\/?>\s*)*<\/h[2-4]>/gi, '');
  cleaned = cleaned.replace(/<p[^>]*>\s*(<br\s*\/?>\s*)*<\/p>/gi, '');
  return cleaned;
};

const getYouTubeEmbedUrl = (url) => {
  if (!isValidString(url)) return null;
  try {
    const u = new URL(url, 'https://example.com');
    if (u.hostname.includes('youtu.be')) {
      const shortId = u.pathname.slice(1);
      if (shortId) return `https://www.youtube.com/embed/${shortId}`;
    }
    if (
      u.hostname.includes('youtube.com') ||
      u.hostname.includes('www.youtube.com')
    ) {
      const v = u.searchParams.get('v');
      if (v) return `https://www.youtube.com/embed/${v}`;
      if (u.pathname.startsWith('/embed/')) return url;
    }
    const match = String(url).match(
      /(?:v=|v\/|embed\/|youtu\.be\/)([A-Za-z0-9_-]{11})/
    );
    if (match?.[1]) return `https://www.youtube.com/embed/${match[1]}`;
    return null;
  } catch {
    const match = String(url).match(
      /(?:v=|v\/|embed\/|youtu\.be\/)([A-Za-z0-9_-]{11})/
    );
    if (match?.[1]) return `https://www.youtube.com/embed/${match[1]}`;
    return null;
  }
};

/* ========================== ASSET IMAGE WITH FALLBACK ========================== */
function AssetImage({ src, fallbackSrc, alt, style }) {
  const [imgSrc, setImgSrc] = useState(src);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    setImgSrc(src);
    setFailed(false);
  }, [src]);

  const handleError = () => {
    if (!failed && fallbackSrc && imgSrc !== fallbackSrc) {
      setImgSrc(fallbackSrc);
      setFailed(true);
    } else {
      setImgSrc(null);
    }
  };

  if (!imgSrc) return null;
  return <img src={imgSrc} alt={alt} style={style} onError={handleError} />;
}

/* ========================== COMMENT ITEM ========================== */
const CommentItem = ({ comment, onReply, replyText, setReplyText }) => {
  const [showReply, setShowReply] = useState(false);

  const avatarInitials = (name) =>
    (name || 'G')
      .split(' ')
      .map((w) => w[0])
      .slice(0, 2)
      .join('')
      .toUpperCase();

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  return (
    <div style={commentStyles.commentBlock}>
      <div style={commentStyles.commentHeader}>
        <div style={commentStyles.avatar}>
          {avatarInitials(comment.user_name)}
        </div>
        <div>
          <p style={commentStyles.commentAuthor}>{comment.user_name}</p>
          <p style={commentStyles.commentDate}>
            {formatDate(comment.created_at)}
          </p>
        </div>
      </div>

      <p style={commentStyles.commentText}>{comment.comment_text}</p>

      <button
        style={commentStyles.replyToggle}
        onClick={() => setShowReply((v) => !v)}
      >
        {showReply ? 'Cancel' : 'Reply'}
      </button>

      {showReply && (
        <div style={commentStyles.replyBox}>
          <input
            value={replyText[comment.id] || ''}
            onChange={(e) =>
              setReplyText((prev) => ({
                ...prev,
                [comment.id]: e.target.value,
              }))
            }
            placeholder="Write a reply..."
            style={commentStyles.replyInput}
          />
          <button
            style={commentStyles.replySubmit}
            onClick={() => {
              onReply(comment.id);
              setShowReply(false);
            }}
          >
            Post Reply
          </button>
        </div>
      )}

      {comment.children?.length > 0 && (
        <div style={commentStyles.childrenWrap}>
          {comment.children.map((child) => (
            <CommentItem
              key={child.id}
              comment={child}
              onReply={onReply}
              replyText={replyText}
              setReplyText={setReplyText}
            />
          ))}
        </div>
      )}
    </div>
  );
};

/* ========================== MAIN COMPONENT ========================== */
export default function OfferAndNewsDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);

  const contentRef = useRef(null);
  const mainRef = useRef(null);

  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' ? window.innerWidth <= 768 : true
  );
  const [loading, setLoading] = useState(true);
  const [entry, setEntry] = useState(null);
  const [contentType, setContentType] = useState('');
  const [headings, setHeadings] = useState([]);
  const [htmlWithIds, setHtmlWithIds] = useState('');
  const [activeId, setActiveId] = useState('section-0');
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [replyText, setReplyText] = useState({});
  const [showLoginModal, setShowLoginModal] = useState(false);

  /* ── Fetch article ── */
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const res = await fetch(
          `https://app.yaper.co/api/v1/content_pages/${encodeURIComponent(id)}`,
          { cache: 'no-store' }
        );
        const json = await res.json();
        const b = json?.data;

        if (!b) {
          if (!cancelled) setEntry(null);
          return;
        }

        const ctype = String(b?.content_type || '')
          .trim()
          .toLowerCase();
        setContentType(ctype);

        if (ctype === 'cc_blog') {
          navigate(`/cardblogdetails/${encodeURIComponent(b.id)}`, {
            replace: true,
          });
          return;
        }

        const validAssets = (b.assets || []).filter(
          (url) =>
            typeof url === 'string' &&
            url.startsWith('http') &&
            !url.startsWith('data:')
        );

        const bannerUrl = prefixIfRelative(
          b.cardImage || b.banner || b.preview_img || ''
        );

        const normalized = {
          id: b.id,
          title: b.creditcardname || b.heading || 'Details',
          banner: bannerUrl,
          date: normalizeDate(b.updated_at || b.created_at || ''),
          readingTime: b.readTime || b.readingTime || '10 min read',
          content: stripBase64Images(String(b.content || '')),
          youtube: b.youtubeLink || '',
          affiliateLink: b.affiliateLink || '',
          assets: validAssets,
          assetFallback: bannerUrl,
        };

        if (!cancelled) setEntry(normalized);
      } catch (err) {
        console.error('Failed to fetch article:', err);
        if (!cancelled) setEntry(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id, navigate]);

  /* ── Fetch comments ── */
  useEffect(() => {
    if (!entry?.id) return;
    const fetchComments = async () => {
      try {
        const res = await fetch(
          `https://app.yaper.co/api/v1/content_pages/${entry.id}/comments`
        );
        const json = await res.json();
        setComments(json?.data || []);
      } catch (err) {
        console.error('Failed to fetch comments', err);
      }
    };
    fetchComments();
  }, [entry?.id]);

  /* ── Unlock body scroll ── */
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const prev = document.body.style.overflow;
    if (getComputedStyle(document.body).overflow === 'hidden') {
      document.body.style.overflow = 'auto';
    }
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  /* ── Resize listener ── */
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  /* ── Parse HTML + inject heading IDs for TOC ── */
  useEffect(() => {
    if (!entry?.content) {
      setHeadings([]);
      setHtmlWithIds('');
      return;
    }
    const parser = new DOMParser();
    const doc = parser.parseFromString(String(entry.content), 'text/html');
    const h2s = doc.querySelectorAll('h2');
    const tocList = [];
    h2s.forEach((el, index) => {
      const sectionId = `section-${index}`;
      el.id = sectionId;
      tocList.push({
        id: sectionId,
        text: el.textContent || `Section ${index + 1}`,
      });
    });
    setHeadings(tocList);
    setHtmlWithIds(doc.body.innerHTML);
    setActiveId(tocList[0]?.id || 'section-0');
  }, [entry]);

  /* ── Scroll-based TOC highlight ── */
  useEffect(() => {
    if (!headings.length || !htmlWithIds || typeof window === 'undefined')
      return;

    const timer = setTimeout(() => {
      const handleScroll = () => {
        const scrollY = window.scrollY;
        const navbarOffset = 100;

        let currentId = headings[0]?.id;

        headings.forEach(({ id }) => {
          const el = document.getElementById(id);
          if (el) {
            const top = el.getBoundingClientRect().top + scrollY - navbarOffset;
            if (scrollY >= top) {
              currentId = id;
            }
          }
        });

        setActiveId(currentId);
      };

      handleScroll();
      window.addEventListener('scroll', handleScroll, { passive: true });
      return () => window.removeEventListener('scroll', handleScroll);
    }, 300);

    return () => clearTimeout(timer);
  }, [headings, htmlWithIds]);

  /* ── Add comment ── */
  const handleAddComment = async () => {
    if (!user) {
      setShowLoginModal(true);
      return;
    }
    if (!newComment.trim()) return;

    const nameVal =
      `${user.first_name || ''} ${user.last_name || ''}`.trim() ||
      user.name ||
      '';
    const emailVal = user.email || '';

    try {
      await fetch(
        `https://app.yaper.co/api/v1/content_pages/${entry.id}/add_comment`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            user_name: nameVal,
            user_email: emailVal,
            comment_text: newComment,
            parent_id: null,
          }),
        }
      );
      setNewComment('');
      const res = await fetch(
        `https://app.yaper.co/api/v1/content_pages/${entry.id}/comments`
      );
      const json = await res.json();
      setComments(json?.data || []);
    } catch (err) {
      console.error(err);
    }
  };

  /* ── Reply ── */
  const handleReply = async (parentId) => {
    if (!user) {
      setShowLoginModal(true);
      return;
    }
    const text = replyText[parentId];
    if (!text?.trim()) return;

    const nameVal =
      `${user.first_name || ''} ${user.last_name || ''}`.trim() ||
      user.name ||
      '';
    const emailVal = user.email || '';

    try {
      await fetch(
        `https://app.yaper.co/api/v1/content_pages/${entry.id}/add_comment`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            user_name: nameVal,
            user_email: emailVal,
            comment_text: text,
            parent_id: parentId,
          }),
        }
      );
      setReplyText((prev) => ({ ...prev, [parentId]: '' }));
      const res = await fetch(
        `https://app.yaper.co/api/v1/content_pages/${entry.id}/comments`
      );
      const json = await res.json();
      setComments(json?.data || []);
    } catch (err) {
      console.error(err);
    }
  };

  /* ── Build comment tree ── */
  const buildTree = (list) => {
    const map = {};
    const roots = [];
    list.forEach((c) => (map[c.id] = { ...c, children: [] }));
    list.forEach((c) => {
      if (c.parent_id) {
        map[c.parent_id]?.children.push(map[c.id]);
      } else {
        roots.push(map[c.id]);
      }
    });
    return roots;
  };

  const commentTree = buildTree(comments);

  /* ── TOC scroll ── */
  const handleScrollTo = (sectionId) => {
    setActiveId(sectionId);
    if (typeof window === 'undefined') return;
    const target = document.getElementById(sectionId);
    if (target) {
      const yOffset = -90;
      const top = target.getBoundingClientRect().top + window.scrollY + yOffset;
      window.scrollTo({ top, behavior: 'smooth' });
    }
  };

  /* ── Affiliate link ── */
  const handleApplyClick = () => {
    if (!user) {
      navigate('/login');
    } else {
      window.open(entry.affiliateLink, '_blank');
    }
  };

  const youtubeEmbed = getYouTubeEmbedUrl(entry?.youtube || '');
  const applied = isMobile ? stylesMobile : styles;

  if (loading)
    return <div style={{ padding: '2rem', textAlign: 'center' }}>Loading…</div>;
  if (!entry)
    return (
      <p style={{ textAlign: 'center', padding: '2rem' }}>
        Content not found for id "{id}".
      </p>
    );

  return (
    <div style={applied.pageContainer}>
      <div style={applied.inner}>
        <style>{`
          .blog-content img {
            image-rendering: auto;
            -ms-interpolation-mode: bicubic;
            filter: none !important;
            max-width: 100%;
            height: auto;
            backface-visibility: hidden;
          }
          .blog-content h2 {
            font-size: 1.4rem;
            font-weight: 700;
            margin-top: 2rem;
            margin-bottom: 0.75rem;
            color: #0f1724;
          }
          .blog-content h3 {
            font-size: 1.15rem;
            font-weight: 600;
            margin-top: 1.5rem;
            margin-bottom: 0.5rem;
            color: #1f2937;
          }
          .blog-content p { margin-bottom: 1rem; }
          .blog-content ul, .blog-content ol {
            padding-left: 1.5rem;
            margin-bottom: 1rem;
          }
          .blog-content li { margin-bottom: 0.4rem; }
          .blog-content strong { font-weight: 700; }
          .blog-content a { color: #0f4dc2; text-decoration: underline; }
          .comment-textarea:focus,
          .comment-input:focus {
            outline: none;
            border-color: #0f4dc2;
            box-shadow: 0 0 0 3px rgba(15,77,194,0.1);
          }
          .reply-toggle:hover { text-decoration: underline; }
          .submit-btn:hover { background-color: #0d3fa8; }
          .submit-btn:active { transform: scale(0.98); }
        `}</style>

        {/* ── Banner ── */}
        <div style={applied.bannerWrapper}>
          <img
            src={
              entry.banner || 'https://via.placeholder.com/820x520?text=Banner'
            }
            alt={entry.title}
            style={applied.bannerImage}
            loading="eager"
            decoding="async"
          />
          <div style={applied.bannerShade} />
          <div style={applied.bannerOverlay}>
            <div style={applied.bannerContent}>
              <h1 style={applied.bannerTitle}>{entry.title}</h1>
              <p style={applied.bannerMeta}>
                {entry.date.display ? (
                  <>
                    <time dateTime={entry.date.iso || undefined}>
                      {entry.date.display}
                    </time>
                    {' • '}
                    {entry.readingTime}
                  </>
                ) : (
                  entry.readingTime
                )}
                {contentType && contentType !== 'blog' && (
                  <>
                    {' • '}
                    <span style={{ opacity: 0.8, fontStyle: 'italic' }}>
                      {contentType}
                    </span>
                  </>
                )}
              </p>
            </div>
          </div>
        </div>

        {/* ── TOC Mobile ── */}
        {isMobile && headings.length > 0 && (
          <div style={{ padding: '8px 0 0 0' }}>
            <h3 style={applied.sidebarTitle}>In this article</h3>
            <ul style={applied.tocList}>
              {headings.map(({ id, text }) => (
                <li key={id} style={applied.tocItem}>
                  <button
                    onClick={() => handleScrollTo(id)}
                    style={
                      id === activeId
                        ? { ...applied.tocLink, ...applied.tocLinkActive }
                        : applied.tocLink
                    }
                  >
                    {text}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* ── Content + Sidebar ── */}
        <div style={applied.contentArea}>
          <main ref={mainRef} style={applied.contentWrapper}>
            {/* ── Article content ── */}
            <div
              ref={contentRef}
              className="blog-content"
              style={applied.content}
              dangerouslySetInnerHTML={{
                __html: htmlWithIds || entry.content || '',
              }}
            />

            {/* ── YouTube embed ── */}
            {youtubeEmbed && (
              <div style={{ marginTop: '28px', width: '100%' }}>
                <div
                  style={{
                    position: 'relative',
                    paddingBottom: '56.25%',
                    height: 0,
                    overflow: 'hidden',
                    borderRadius: '10px',
                    boxShadow: '0 6px 18px rgba(13,26,72,0.06)',
                    maxWidth: '100%',
                  }}
                >
                  <iframe
                    title="Article video"
                    src={youtubeEmbed}
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      border: 0,
                    }}
                  />
                </div>
              </div>
            )}

            {/* ════ COMMENTS — inside <main> so they align perfectly ════ */}
            <div style={{ marginTop: '48px', paddingBottom: '60px' }}>
              {/* ── Existing comments ── */}
              {commentTree.length > 0 && (
                <div style={{ marginBottom: '36px' }}>
                  <h3 style={commentStyles.sectionTitle}>
                    {commentTree.length} Comment
                    {commentTree.length !== 1 ? 's' : ''}
                  </h3>
                  {commentTree.map((c) => (
                    <CommentItem
                      key={c.id}
                      comment={c}
                      onReply={handleReply}
                      replyText={replyText}
                      setReplyText={setReplyText}
                    />
                  ))}
                </div>
              )}

              {/* ── Leave a Reply form ── */}
              <div style={commentStyles.formCard}>
                <h3 style={commentStyles.formTitle}>Leave a Reply</h3>
                <p style={commentStyles.formSubtitle}>
                  {user ? (
                    <>
                      Commenting as{' '}
                      <strong>
                        {`${user.first_name || ''} ${user.last_name || ''}`.trim() ||
                          user.name}
                      </strong>
                    </>
                  ) : (
                    <>
                      Please{' '}
                      <button
                        onClick={() => setShowLoginModal(true)}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: '#0f4dc2',
                          fontWeight: 700,
                          cursor: 'pointer',
                          fontSize: '13px',
                          padding: 0,
                        }}
                      >
                        log in
                      </button>{' '}
                      to post a comment.
                    </>
                  )}
                </p>

                <textarea
                  className="comment-textarea"
                  value={newComment}
                  onChange={(e) => {
                    if (!user) {
                      setShowLoginModal(true);
                      return;
                    }
                    setNewComment(e.target.value);
                  }}
                  onFocus={() => {
                    if (!user) setShowLoginModal(true);
                  }}
                  placeholder="Type your comments here"
                  rows={6}
                  style={{
                    ...commentStyles.textarea,
                    cursor: user ? 'text' : 'not-allowed',
                    backgroundColor: user ? '#fff' : '#f9fafb',
                  }}
                  readOnly={!user}
                />

                <button
                  className="submit-btn"
                  style={commentStyles.submitButton}
                  onClick={handleAddComment}
                >
                  POST COMMENT
                </button>
              </div>
            </div>
            {/* ════ END COMMENTS ════ */}
          </main>

          {/* ── Sidebar ── */}
          <aside style={applied.sidebar}>
            {headings.length > 0 && !isMobile && (
              <>
                <h3 style={applied.sidebarTitle}>In this article</h3>
                <ul style={applied.tocList}>
                  {headings.map(({ id, text }) => (
                    <li key={id} style={applied.tocItem}>
                      <button
                        onClick={() => handleScrollTo(id)}
                        style={
                          id === activeId
                            ? { ...applied.tocLink, ...applied.tocLinkActive }
                            : applied.tocLink
                        }
                      >
                        {text}
                      </button>
                    </li>
                  ))}
                </ul>
              </>
            )}

            {isValidString(entry.affiliateLink) && (
              <div style={applied.applyCard}>
                <div style={applied.applyHeader}>
                  <strong>Apply for this card</strong>
                </div>
                <p style={applied.applyBody}>
                  Ready to enjoy the benefits? Click Apply to get started.
                </p>
                <button style={applied.applyButton} onClick={handleApplyClick}>
                  APPLY NOW
                </button>
                <p style={applied.smallNote}>
                  By clicking "Apply Now", you'll be redirected to the issuer's
                  secure application page.
                </p>
              </div>
            )}
          </aside>
        </div>
        {/* ── END Content + Sidebar ── */}
      </div>

      {showLoginModal && (
        <LoginModal onClose={() => setShowLoginModal(false)} />
      )}
    </div>
  );
}

/* ========================== PAGE STYLES ========================== */
const styles = {
  pageContainer: {
    backgroundColor: '#f6f7fb',
    fontFamily: '"Inter", "Segoe UI", Tahoma, Geneva, Verdana, sans-serif',
    minHeight: '100vh',
    color: '#111827',
    paddingBottom: '60px',
  },
  inner: { maxWidth: '1200px', margin: '0 auto' },
  bannerWrapper: {
    position: 'relative',
    width: '100%',
    height: '520px',
    maxWidth: '820px',
    margin: '24px auto',
    borderRadius: '12px',
    overflow: 'hidden',
    boxShadow: '0 10px 40px rgba(13, 26, 72, 0.08)',
    backgroundColor: '#0b0f1a',
  },
  bannerImage: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    objectPosition: 'center',
    display: 'block',
    filter: 'none',
    imageRendering: 'auto',
    backfaceVisibility: 'hidden',
    backgroundColor: '#0b0f1a',
  },
  bannerShade: {
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: 0,
    top: 0,
    background:
      'linear-gradient(to top, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.2) 40%, rgba(0,0,0,0) 70%)',
    pointerEvents: 'none',
  },
  bannerOverlay: {
    position: 'absolute',
    left: 0,
    bottom: 0,
    width: '100%',
    padding: '28px 36px',
    boxSizing: 'border-box',
    display: 'flex',
    alignItems: 'flex-end',
    pointerEvents: 'none',
  },
  bannerContent: {
    color: '#fff',
    pointerEvents: 'auto',
    background:
      'linear-gradient(90deg, rgba(255,255,255,0.06), rgba(255,255,255,0.02))',
    padding: '16px 18px',
    borderRadius: '10px',
    maxWidth: '60%',
    boxShadow: '0 6px 18px rgba(2,6,23,0.18)',
  },
  bannerTitle: {
    fontSize: '1.6rem',
    fontWeight: 700,
    margin: 0,
    lineHeight: 1.2,
  },
  bannerMeta: { marginTop: '6px', fontSize: '13px', color: '#e6eefc' },
  contentArea: {
    display: 'flex',
    gap: '28px',
    padding: '36px 16px',
    maxWidth: '1200px',
    margin: '0 auto',
    boxSizing: 'border-box',
  },
  contentWrapper: {
    flex: 3,
    backgroundColor: '#ffffff',
    padding: '36px',
    borderRadius: '12px',
    boxShadow: '0 6px 18px rgba(13, 26, 72, 0.04)',
    minHeight: '200px',
    overflow: 'visible',
  },
  content: { fontSize: '16px', color: '#1f2937', lineHeight: '1.78' },
  sidebar: {
    flex: 1,
    maxWidth: '320px',
    backgroundColor: 'transparent',
    position: 'sticky',
    top: '100px',
    alignSelf: 'flex-start',
    height: 'fit-content',
    zIndex: 1,
  },
  sidebarTitle: {
    fontSize: '15px',
    fontWeight: 700,
    marginBottom: '12px',
    color: '#0f1724',
  },
  tocList: { listStyle: 'none', padding: 0, margin: 0 },
  tocItem: { marginBottom: '10px' },
  tocLink: {
    textDecoration: 'none',
    background: 'none',
    border: 'none',
    color: '#374151',
    fontSize: '14px',
    textAlign: 'left',
    cursor: 'pointer',
    padding: '8px 12px',
    display: 'block',
    width: '100%',
    borderRadius: '8px',
  },
  tocLinkActive: {
    color: '#0f4dc2',
    fontWeight: 700,
    backgroundColor: 'rgba(14,77,194,0.06)',
    borderLeft: '3px solid #0f4dc2',
    paddingLeft: '10px',
  },
  applyCard: {
    marginTop: '22px',
    background: '#ffffff',
    borderRadius: '10px',
    padding: '18px',
    boxShadow: '0 6px 18px rgba(13, 26, 72, 0.04)',
    border: '1px solid rgba(15,77,194,0.06)',
  },
  applyHeader: { fontSize: '14px', marginBottom: '8px', color: '#0b2548' },
  applyBody: { fontSize: '13px', color: '#374151', marginBottom: '12px' },
  applyButton: {
    display: 'inline-block',
    textDecoration: 'none',
    padding: '10px 16px',
    borderRadius: '8px',
    fontWeight: 700,
    color: '#fff',
    backgroundColor: '#0f4dc2',
    textAlign: 'center',
    border: 'none',
    cursor: 'pointer',
  },
  smallNote: { marginTop: '10px', fontSize: '12px', color: '#6b7280' },
};

const stylesMobile = {
  ...styles,
  inner: { padding: '12px', maxWidth: '1000px', margin: '0 auto' },
  bannerWrapper: {
    ...styles.bannerWrapper,
    height: '260px',
    marginTop: '0',
    borderRadius: '0',
    maxWidth: '100%',
  },
  bannerImage: {
    ...styles.bannerImage,
    objectFit: 'cover',
  },
  bannerOverlay: {
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: 0,
    padding: '16px',
    pointerEvents: 'none',
  },
  bannerContent: {
    pointerEvents: 'auto',
    background: 'rgba(0,0,0,0.35)',
    backdropFilter: 'blur(6px)',
    WebkitBackdropFilter: 'blur(6px)',
    padding: '12px 14px',
    borderRadius: '10px',
    color: '#fff',
    maxWidth: '100%',
  },
  bannerTitle: {
    fontSize: '1.1rem',
    fontWeight: 700,
    margin: 0,
    color: '#ffffff',
    lineHeight: 1.3,
  },
  bannerMeta: { marginTop: '4px', fontSize: '12px', color: '#e0e7ff' },
  contentArea: {
    display: 'block',
    gap: 0,
    padding: '16px 0',
    width: '100%',
    boxSizing: 'border-box',
  },
  contentWrapper: {
    backgroundColor: '#ffffff',
    padding: '18px',
    borderRadius: '10px',
    boxShadow: 'none',
    overflow: 'visible',
  },
};

/* ========================== COMMENT STYLES ========================== */
const commentStyles = {
  sectionTitle: {
    fontSize: '18px',
    fontWeight: 700,
    color: '#0f1724',
    marginBottom: '20px',
  },
  commentBlock: {
    backgroundColor: '#ffffff',
    borderRadius: '10px',
    padding: '18px 20px',
    marginBottom: '14px',
    border: '1px solid rgba(15,77,194,0.07)',
    boxShadow: '0 2px 8px rgba(13,26,72,0.04)',
  },
  commentHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '10px',
  },
  avatar: {
    width: '40px',
    height: '40px',
    borderRadius: '50%',
    backgroundColor: '#e6f1fb',
    color: '#185fa5',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 700,
    fontSize: '13px',
    flexShrink: 0,
  },
  commentAuthor: {
    margin: 0,
    fontWeight: 700,
    fontSize: '14px',
    color: '#0f1724',
  },
  commentDate: {
    margin: 0,
    fontSize: '12px',
    color: '#6b7280',
  },
  commentText: {
    fontSize: '14px',
    color: '#374151',
    lineHeight: '1.65',
    margin: '0 0 10px 0',
  },
  replyToggle: {
    background: 'none',
    border: 'none',
    color: '#0f4dc2',
    fontSize: '13px',
    fontWeight: 600,
    cursor: 'pointer',
    padding: '0',
  },
  replyBox: {
    marginTop: '12px',
    display: 'flex',
    gap: '10px',
    alignItems: 'center',
  },
  replyInput: {
    flex: 1,
    padding: '8px 12px',
    borderRadius: '8px',
    border: '1px solid #d1d5db',
    fontSize: '13px',
    outline: 'none',
    color: '#111827',
    fontFamily: 'inherit',
  },
  replySubmit: {
    padding: '8px 14px',
    borderRadius: '8px',
    backgroundColor: '#0f4dc2',
    color: '#fff',
    border: 'none',
    fontSize: '13px',
    fontWeight: 700,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    fontFamily: 'inherit',
  },
  childrenWrap: {
    marginTop: '14px',
    paddingLeft: '20px',
    borderLeft: '2px solid #e6f1fb',
  },
  formCard: {
    backgroundColor: '#EEF7FE',
    borderRadius: '12px',
    padding: '28px 32px',
    border: '1px solid rgba(15,77,194,0.08)',
    boxShadow: '0 4px 14px rgba(13,26,72,0.05)',
  },
  formTitle: {
    fontSize: '18px',
    fontWeight: 700,
    color: '#0f1724',
    margin: '0 0 6px 0',
  },
  formSubtitle: {
    fontSize: '13px',
    color: '#6b7280',
    margin: '0 0 20px 0',
  },
  textarea: {
    width: '100%',
    padding: '12px 14px',
    borderRadius: '8px',
    border: '1px solid #d1d5db',
    fontSize: '14px',
    color: '#111827',
    resize: 'vertical',
    boxSizing: 'border-box',
    fontFamily: 'inherit',
    lineHeight: '1.6',
    marginBottom: '16px',
    display: 'block',
  },
  submitButton: {
    backgroundColor: '#1D4094',
    color: '#ffffff',
    border: 'none',
    borderRadius: '6px',
    padding: '13px 28px',
    fontSize: '13px',
    fontWeight: 700,
    letterSpacing: '0.06em',
    cursor: 'pointer',
    display: 'block',
    width: '100%',
    fontFamily: 'inherit',
    transition: 'background-color 0.15s ease',
  },
};
